import React, { useContext, useRef, useState } from 'react'
import {
  Flex,
  space,
  PrimaryButton,
  SecondaryButton,
  FormField,
  TextArea,
  FormFieldLabelPosition,
  Box
} from '@workday/canvas-kit-react'
import { plusIcon, trashIcon } from '@workday/canvas-system-icons-web'
import { ScreenWidthCategoryContext, ScreenWidthCategory } from '../../utils/ScreenWidthCategoryContext'
import { PrimaryButtonColors } from '../../components/CanvasThemes'
import { PPLoadingDots } from '../../components/PPLoadingDots'
import { AttachmentData, attachmentURL, IncidentPostData, isImageAttachment } from '../../models/IncidentPostData'

export interface EditIncidentPostFormProps {
  incidentPost: IncidentPostData
  onSubmit: (incidentPost: IncidentPostData, callback: (success: boolean) => void) => void
  onCancel: () => void
}

const EditIncidentPostForm = ({ incidentPost, onSubmit, onCancel }: EditIncidentPostFormProps) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const screenWidthCategory = useContext(ScreenWidthCategoryContext)
  const labelPosition =
    screenWidthCategory <= ScreenWidthCategory.ExtraSmall ? FormFieldLabelPosition.Top : FormFieldLabelPosition.Left
  const imageRef = useRef<any>(null)
  const [initialIncidentPost, setInitialIncidentPost] = useState(incidentPost)
  const [enteredMessage, setEnteredMessage] = useState<string>(incidentPost.message)
  const [enteredAttachments, setEnteredAttachments] = useState<AttachmentData[]>(incidentPost.attachments)
  const [enteredImagesToAdd, setEnteredImagesToAdd] = useState<string[]>([])
  if (incidentPost.message !== initialIncidentPost.message) {
    setEnteredMessage(incidentPost.message)
  }
  if (incidentPost.attachments !== initialIncidentPost.attachments) {
    setEnteredAttachments(incidentPost.attachments)
  }
  if (initialIncidentPost.id !== incidentPost.id) {
    setEnteredImagesToAdd([])
  }
  if (initialIncidentPost !== incidentPost) {
    setInitialIncidentPost(incidentPost)
  }

  const onFormSubmit = (event: any) => {
    const editedIncidentPost: IncidentPostData = {
      id: incidentPost.id,
      incidentID: incidentPost.incidentID,
      message: enteredMessage,
      attachments: enteredAttachments,
      imagesToAdd: enteredImagesToAdd.map(image => {
        const parts = image.split(',')
        return parts[parts.length - 1]
      })
    }
    setLoadingSubmit(true)
    onSubmit(editedIncidentPost, success => {
      setLoadingSubmit(false)
    })
  }

  const initiatePhotoPicker = () => {
    imageRef.current.click()
  }

  const onPostPhotoFileChange: React.ChangeEventHandler<HTMLInputElement> = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files != null) {
      const file = event.target.files[0]
      const fileReader = new FileReader()
      fileReader.onloadend = (ev: ProgressEvent<FileReader>) => {
        const result = ev.target?.result
        if (!result) {
          return
        }
        const dataURL = result as string
        setEnteredImagesToAdd(prev => [...prev, dataURL])
      }
      fileReader.readAsDataURL(file)
    }
  }

  return (
    <Box>
      <FormField label="Message" labelPosition={labelPosition}>
        <TextArea
          value={enteredMessage}
          onChange={event => setEnteredMessage(event.currentTarget.value)}
          style={{ minHeight: '300px' }}
        />
      </FormField>
      <FormField label="Photos" labelPosition={labelPosition}>
        <Flex flexDirection="column" gap={space.xs} alignItems="center">
          {enteredAttachments.map((attachment, idx) =>
            isImageAttachment(attachment) ? (
              <Flex flexDirection="row" gap={space.xxs} alignItems="end" key={attachment.File}>
                <img
                  src={attachmentURL(attachment.File, attachment.Type, 200, 0)}
                  srcSet={`${attachmentURL(attachment.File, attachment.Type, 400, 0)} 2x`}
                  alt="Photo"
                  width={200}
                />
                <SecondaryButton
                  icon={trashIcon}
                  size="extraSmall"
                  onClick={() =>
                    setEnteredAttachments(prev => {
                      return prev.filter((value, prevIdx) => prevIdx !== idx)
                    })
                  }
                />
              </Flex>
            ) : (
              <></>
            )
          )}
          {enteredImagesToAdd.map((image, idx) => (
            <Flex flexDirection="row" gap={space.xxs} alignItems="end" key={image}>
              <img src={image} alt="Photo" width={200} />
              <SecondaryButton
                icon={trashIcon}
                size="extraSmall"
                onClick={() =>
                  setEnteredImagesToAdd(prev => {
                    return prev.filter((value, prevIdx) => prevIdx !== idx)
                  })
                }
              />
            </Flex>
          ))}
          <SecondaryButton icon={plusIcon} size="extraSmall" onClick={initiatePhotoPicker}>
            Add Photo
          </SecondaryButton>
        </Flex>
      </FormField>
      {loadingSubmit ? (
        <PPLoadingDots />
      ) : (
        <Flex flexDirection="row" columnGap="s">
          <PrimaryButton colors={PrimaryButtonColors} onClick={onFormSubmit}>
            Submit
          </PrimaryButton>
          <SecondaryButton onClick={() => onCancel()}>Cancel</SecondaryButton>
        </Flex>
      )}

      <input
        type="file"
        name="image"
        accept="image/png, image/jpeg"
        ref={imageRef}
        onChange={onPostPhotoFileChange}
        hidden
      />
    </Box>
  )
}

export default EditIncidentPostForm
